import { parseCartItemId, type PriceChange } from '@/lib/cart-utils';
import { trpcClient } from '@/lib/trpc';
import { useCartStore } from '@/stores/cart-store';

export function useValidatePricesOnSubmit() {
  const items = useCartStore(state => state.items);
  const updateItemPrice = useCartStore(state => state.updateItemPrice);
  const updateItemOption = useCartStore(state => state.updateItemOption);
  const updateItemSelectedOptions = useCartStore(state => state.updateItemSelectedOptions);

  const validatePrices = async (): Promise<{
    hasChanges: boolean;
    priceChanges: PriceChange[];
  }> => {
    if (items.length === 0) {
      return { hasChanges: false, priceChanges: [] };
    }

    const queryInput = {
      items: items.map(item => parseCartItemId(item.id)),
    };

    const data = await trpcClient.menu.validateCartPrices.query(queryInput);

    const priceChanges: PriceChange[] = [];

    items.forEach(item => {
      const { menuId, optionIds } = parseCartItemId(item.id);
      const menuData = data.menus[menuId];

      if (!menuData || !menuData.isAvailable) {
        return;
      }

      const basePrice = menuData.price;
      const optionTotal = optionIds.reduce((sum, optId) => sum + (data.options[optId]?.additionalPrice ?? 0), 0);
      const newPrice = basePrice + optionTotal;

      if (newPrice !== item.price) {
        priceChanges.push({
          id: item.id,
          name: item.name,
          oldPrice: item.price,
          newPrice,
          option: item.option,
        });
      }
    });

    return {
      hasChanges: priceChanges.length > 0,
      priceChanges,
    };
  };

  const applyPriceUpdates = async (priceChanges: PriceChange[]) => {
    if (priceChanges.length === 0) return;

    const queryInput = {
      items: items.map(item => parseCartItemId(item.id)),
    };

    const data = await trpcClient.menu.validateCartPrices.query(queryInput);

    priceChanges.forEach(change => {
      updateItemPrice(change.id, change.newPrice);

      const { optionIds } = parseCartItemId(change.id);
      if (optionIds.length > 0) {
        const cartItem = items.find(candidate => candidate.id === change.id);
        const newOptionTexts = optionIds
          .map(optId => {
            const opt = data.options[optId];
            if (!opt) return null;
            return opt.additionalPrice > 0
              ? `${opt.optionValue} (+${opt.additionalPrice.toLocaleString()}원)`
              : opt.optionValue;
          })
          .filter((text): text is string => text !== null);

        const newOptionText = newOptionTexts.join(', ');
        if (newOptionText) {
          updateItemOption(change.id, newOptionText);
        }

        if (cartItem?.selectedOptions) {
          updateItemSelectedOptions(
            change.id,
            cartItem.selectedOptions.map(option => {
              const currentOption = data.options[option.option_id];
              return currentOption
                ? {
                    ...option,
                    option_value: currentOption.optionValue,
                    additional_price: currentOption.additionalPrice,
                  }
                : option;
            })
          );
        }
      }
    });
  };

  return {
    validatePrices,
    applyPriceUpdates,
  };
}
